/**
 * FeeBarChart — public-list-rate comparison of Bidorah's seller fees
 * vs every major live-commerce / resale marketplace, named explicitly.
 *
 * Why named (not "Others"): sellers know these brands. Putting our 5%
 * next to Whatnot's 10.9% and eBay's 13.25% in a bar chart is the most
 * brutal, undeniable sales pitch we have. Every blade of marketing is
 * downstream of "you keep more money".
 *
 * Numbers are public list rates (incl. payment processing where the
 * platform lumps it in). Updated whenever a competitor moves theirs.
 * Source comments live alongside each entry so this file stays
 * defensible.
 *
 * Animation: bars grow on mount with a staggered delay. CSS-only,
 * GPU-cheap (height transition). Respects `prefers-reduced-motion`.
 */
import React, { useEffect, useRef, useState } from "react";
import { CrownSimpleIcon } from "@phosphor-icons/react";

const COMPETITORS = [
  // Bidorah's published retail commission is 5% flat (no separate
  // payment-processing surcharge — payment fees are absorbed). We show
  // 5% as the headline so a casual reader compares apples-to-apples.
  {
    name: "Bidorah",
    pct: 5.0,
    color: "from-emerald-300 to-emerald-500",
    accent: "text-emerald-300",
    border: "border-emerald-400/70",
    isUs: true,
    note: "All-in. 4.5% for wholesale.",
  },
  // Palmstreet publishes 6.9% on their public pricing page.
  { name: "Palmstreet", pct: 6.9, color: "from-zinc-400 to-zinc-600", accent: "text-zinc-300" },
  // Whatnot: 8% commission + 2.9% payment processing = 10.9% effective.
  { name: "Whatnot", pct: 10.9, color: "from-zinc-400 to-zinc-600", accent: "text-zinc-300" },
  // Mercari: 10% selling fee + 2.9% + $0.50 payment processing.
  { name: "Mercari", pct: 12.9, color: "from-zinc-400 to-zinc-600", accent: "text-zinc-300" },
  // StockX: 9.5% transaction + 3% payment processing = 12.5%.
  { name: "StockX", pct: 12.5, color: "from-zinc-400 to-zinc-600", accent: "text-zinc-300" },
  // eBay Trading Cards: ~13.25% (incl payment processing) for most categories.
  { name: "eBay", pct: 13.25, color: "from-zinc-400 to-zinc-600", accent: "text-zinc-300" },
];

const MAX_PCT = Math.max(...COMPETITORS.map((c) => c.pct));

export default function FeeBarChart() {
  const [grown, setGrown] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setGrown(true);
      return undefined;
    }
    // Trigger growth animation once the chart is on-screen.
    const el = rootRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setGrown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setGrown(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div className="relative" ref={rootRef} data-testid="fee-bar-chart">
      {/* Whisper-quiet grid lines behind the bars for visual depth. */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to top, transparent 0, transparent 31px, rgba(255,255,255,0.5) 31px, rgba(255,255,255,0.5) 32px)",
        }}
        aria-hidden
      />

      {/* Callout speech-bubble above the Bidorah bar */}
      <div className="relative flex items-end justify-between gap-3 sm:gap-4 h-[320px] sm:h-[380px] pt-12">
        {COMPETITORS.map((c, i) => {
          const h = (c.pct / MAX_PCT) * 100;
          return (
            <div key={c.name} className="flex-1 flex flex-col items-center gap-2 min-w-0">
              {/* Percentage label sitting just above the bar tip */}
              <div
                className={`text-[11px] sm:text-sm font-black tabular-nums transition-opacity duration-700 ${c.isUs ? c.accent : "text-white/55"}`}
                style={{ transitionDelay: `${800 + i * 80}ms`, opacity: grown ? 1 : 0 }}
              >
                {c.pct}%
              </div>

              {/* The bar */}
              <div className="relative w-full max-w-[64px] sm:max-w-[88px] flex-1 flex flex-col-reverse">
                <div
                  className={`relative w-full rounded-t-xl bg-gradient-to-t ${c.color} ${c.isUs ? `ring-2 ring-offset-2 ring-offset-zinc-950 ${c.border}` : ""}`}
                  style={{
                    height: grown ? `${h}%` : "0%",
                    transition: `height 900ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 120}ms`,
                  }}
                >
                  {/* Crown sticker on the Bidorah (winner) bar */}
                  {c.isUs && (
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 h-7 w-7 rounded-full bg-amber-400 text-emerald-950 grid place-items-center shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-950"
                      aria-hidden
                    >
                      <CrownSimpleIcon size={14} weight="fill" />
                    </div>
                  )}
                  {/* Vertical "OTHERS" / "BIDORAH" label inside the bar */}
                  <div
                    className={`absolute inset-0 flex items-center justify-center pointer-events-none ${c.isUs ? "text-emerald-950" : "text-zinc-900/70"}`}
                  >
                    <span
                      className="font-display font-black tracking-tight text-[11px] sm:text-sm whitespace-nowrap"
                      style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                    >
                      {c.name.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Name beneath the bar (mirrors the in-bar label for legibility on mobile) */}
              <div className={`text-[10px] sm:text-xs font-bold tracking-wide ${c.isUs ? c.accent : "text-white/55"} truncate w-full text-center`}>
                {c.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footnote */}
      <p className="mt-6 text-[10px] sm:text-xs text-white/45 leading-relaxed max-w-md">
        Rates are public list prices including payment processing where the
        platform lumps it in. Bidorah's 5% is all-in — no processing surcharge.
        Wholesale (B2B) sellers pay 4.5%. Last reviewed Feb 2026.
      </p>
    </div>
  );
}
